/**
 * Axios clients for REACT_APP_API_BASE_URL — attach Firebase id token on every request.
 */
import { API_BASE_URL } from '../config/api';
import { createTrackedAxiosInstance } from './apiInterceptor';
import { clearAuthSession, getAuthToken } from './authSession';

export const JSON_HEADERS = { 'Content-Type': 'application/json' };

const applyBearerHeader = (config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

const redirectToLogin = () => {
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
};

const handleUnauthorized = (error) => {
  if (error.response?.status === 401) {
    clearAuthSession();
    redirectToLogin();
  }
  return Promise.reject(error);
};

/**
 * @param {import('axios').AxiosInstance} client
 * @param {{ requireAuth?: boolean }} [options]
 */
export const attachBearerAuthInterceptor = (client, options = {}) => {
  const { requireAuth = false } = options;

  client.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      if (requireAuth && !token) {
        return Promise.reject(new Error('User not authenticated'));
      }
      return applyBearerHeader(config);
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use((response) => response, handleUnauthorized);
  return client;
};

/**
 * Tracked axios instance for Companies / watchlist / batch APIs (REACT_APP_API_BASE_URL).
 */
const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

export const createAuthenticatedApiClient = (config = {}, options = {}) => {
  const client = createTrackedAxiosInstance({
    headers: JSON_HEADERS,
    timeout: DEFAULT_REQUEST_TIMEOUT_MS,
    ...config,
  });
  attachBearerAuthInterceptor(client, options);
  return client;
};

/**
 * @param {string} userId
 * @param {string} pathAfterUsers — e.g. `batch`, `tenant-company`, `companies`
 */
export const createUserApiClient = (userId, pathAfterUsers, options = {}) =>
  createAuthenticatedApiClient(
    {
      baseURL: `${API_BASE_URL}/api/v1/users/${encodeURIComponent(userId)}/${pathAfterUsers}`,
    },
    options
  );
